import styled from "styled-components";
import PageWrapper from "../../common/components/PageWrapper";
import BasicButton from "../../common/components/BasicButton";
import InfohubDocumentTable from "../component/InfohubDocumentTable";
import InfohubUploadModal from "../component/InfohubUploadModal";
import InfohubDeleteModal from "../component/InfohubDeleteModal";
import useInfohubContainer from "./InfohubContainer.hook";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

function InfohubContainer() {
  const {
    models: {
      documents,
      loading,
      deleteLoading,
      currentPage,
      totalPages,
      isUploadModalOpen,
      isDeleteModalOpen,
      selectedDocument,
    },
    operations: {
      handleUpload,
      handleDelete,
      handleDeleteClick,
      handlePageChange,
      setIsUploadModalOpen,
      setIsDeleteModalOpen,
      setSelectedDocument,
    },
  } = useInfohubContainer();

  return (
    <>
      <InfohubUploadModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />
      <InfohubDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedDocument(null);
        }}
        onDelete={handleDelete}
        deleteLoading={deleteLoading}
        documentName={selectedDocument?.fileName}
      />
      <PageWrapper isColumnFlex>
        <PageContents>
          <HeaderSection>
            <TitleRow>
              <Title>문서관리</Title>
              <TooltipWrapper>
                <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                <TooltipContent>
                  • 최대 100MB의 파일까지 허용하며, 문서 처리 상태는 아래와 같습니다.
                  <br />
                  &nbsp;&nbsp;준비 중 &gt; 텍스트 추출 &gt; 텍스트 분리 &gt; 임베딩 생성 &gt; 처리 완료/실패
                  <br />
                  • 처리에 실패한 문서는 삭제 후 다시 업로드를 진행하여야 합니다.
                  <br />
                  • 처리 상태는 5초 간격으로 실시간 업데이트됩니다.
                </TooltipContent>
              </TooltipWrapper>
            </TitleRow>
            <ButtonWrapper>
              <BasicButton
                text="업로드"
                onClick={() => setIsUploadModalOpen(true)}
              />
            </ButtonWrapper>
          </HeaderSection>
          <TableWrapper>
            <InfohubDocumentTable
              documents={documents}
              isLoading={loading}
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onDelete={handleDeleteClick}
            />
          </TableWrapper>
        </PageContents>
      </PageWrapper>
    </>
  );
}

const PageContents = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  backgroundColor: theme.color.white,
  borderRadius: 10,
  padding: theme.spacing.regular,
  gap: theme.spacing.regular,
}));

const HeaderSection = styled.div(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
}));

const TitleRow = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const Title = styled.h1(({ theme }) => ({
  margin: 0,
  fontSize: 28,
  fontWeight: "bold",
  color: theme.color.black,
}));

const TooltipWrapper = styled.div(() => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  cursor: "pointer",
  "&:hover > div": {
    opacity: 1,
    visibility: "visible",
  },
}));

const TooltipContent = styled.div(() => ({
  position: "absolute",
  left: "calc(100% + 8px)",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "#1F2937",
  color: "white",
  padding: "12px 16px",
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.6,
  whiteSpace: "nowrap",
  zIndex: 1000,
  opacity: 0,
  visibility: "hidden",
  transition: "opacity 0.2s, visibility 0.2s",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  "&::before": {
    content: '""',
    position: "absolute",
    right: "100%",
    top: "50%",
    transform: "translateY(-50%)",
    borderWidth: "6px 6px 6px 0",
    borderStyle: "solid",
    borderColor: "transparent #1F2937 transparent transparent",
  },
}));

const ButtonWrapper = styled.div(() => ({
  display: "flex",
  gap: 10,
}));

const TableWrapper = styled.div(() => ({
  flex: 1,
  width: "100%",
  overflow: "hidden",
}));

export default InfohubContainer;
